import * as React from 'react';
import { FormHelperText, FormLabel, Box, FormControl, Rating as MuiRating } from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import { useMemo, useState } from 'react';

interface RatingProps {
    onChange: (event: React.SyntheticEvent, value: number | null) => void;
    label?: string;
    description?: string;
    required?: boolean;
    value: number | string | null;
    error?: string;
    minimum?: number;
    maximum?: number;
    enabled?: boolean;
    visible?: boolean;
    appValidated?: boolean;
    precision?: number;
    scope?: string;
    size?: 'small' | 'medium' | 'large';
}

const Rating = (props: RatingProps) => {
    const {
        onChange,
        label,
        description,
        required = false,
        value,
        error,
        minimum = 0,
        maximum = 5,
        enabled = true,
        visible = true,
        appValidated = false,
        precision = 1,
        scope,
        size = 'large'
    } = props;

    const [appHasBeenValidated, setAppHasBeenValidated] = useState(false);

    React.useEffect(() => {
        if (appValidated) {
            setAppHasBeenValidated(true);
        }
    }, [appValidated]);

    const showDescription = useMemo(() =>
            description !== undefined && description !== "",
        [description]
    );

    const numericValue = useMemo(() => {
        if (value === null || value === '' || value === undefined) {
            return null;
        }
        if (typeof value === 'number') {
            return value;
        }
        const parsed = parseFloat(value as string);
        return isNaN(parsed) ? null : parsed;
    }, [value]);

    const isEmpty = useMemo(() =>
            numericValue === null,
        [numericValue]
    );

    const showRequiredError = useMemo(() =>
            required && appHasBeenValidated && isEmpty,
        [required, appHasBeenValidated, isEmpty]
    );

    const { computedError, showError } = useMemo(() => {
        if (!appHasBeenValidated && isEmpty) return {
            computedError: '',
            showError: false
        };

        let computedError = error;

        if (showRequiredError) {
            computedError = 'Please provide a rating';
        }
        else if (numericValue !== null) {
            if (numericValue < minimum) {
                computedError = `Rating must be at least ${minimum}`;
            } else if (numericValue > maximum) {
                computedError = `Rating must be at most ${maximum}`;
            }
        }

        const showError = computedError !== undefined && computedError !== "";
        return { computedError: computedError || '', showError };
    }, [error, numericValue, minimum, maximum, showRequiredError, appHasBeenValidated, isEmpty]);

    const shouldSpanFullWidth = useMemo(() => maximum >= 3, [maximum]);

    const dynamicFontSize = useMemo(() => {
        if (!shouldSpanFullWidth) return undefined;
        if (maximum === 3) return '4rem';
        if (maximum === 4) return '3rem';
        if (maximum === 5) return '2.5rem';
        if (maximum >= 6) return `${12 / maximum}rem`;
        return undefined;
    }, [maximum, shouldSpanFullWidth]);

    if (!visible) return null;

    return (
        <Box sx={{ display: 'block' }}>
            <FormControl className="form-control" fullWidth={true} error={showError}>
                <FormLabel
                    required={required}
                    color="secondary"
                    className="form-separate-label"
                    error={showError}
                >
                    {label}
                </FormLabel>
                <Box sx={{
                    mt: 1,
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    position: 'relative'
                }}>
                    <MuiRating
                        name={scope}
                        value={numericValue}
                        onChange={onChange}
                        precision={precision}
                        max={maximum}
                        size={size}
                        disabled={!enabled}
                        emptyIcon={<StarIcon style={{ opacity: 0.55 }} fontSize="inherit" />}
                        emptyLabelText="" // Remove the "Empty" text
                        sx={{
                            opacity: enabled ? 1 : 0.6,
                            pointerEvents: enabled ? 'auto' : 'none',
                            ...(shouldSpanFullWidth && {
                                width: '100%',
                                maxWidth: '100%',
                                display: 'flex !important',
                                justifyContent: 'space-evenly',
                                fontSize: dynamicFontSize,
                                position: 'relative',
                                zIndex: 1,
                                '& > label': {
                                    cursor: 'pointer'
                                },
                                '& .MuiRating-label': {
                                    cursor: 'pointer'
                                },
                                '& .MuiRating-icon': {
                                    margin: 0,
                                    cursor: 'pointer'
                                },
                                // Target and completely hide the pristine/empty label
                                '& > label.MuiRating-pristine': {
                                    display: 'none !important',
                                    visibility: 'hidden !important',
                                    width: '0 !important',
                                    height: '0 !important',
                                    margin: '0 !important',
                                    padding: '0 !important',
                                    position: 'absolute !important',
                                    left: '-9999px !important'
                                },
                                // Also hide by input value
                                '& input[value=""]': {
                                    display: 'none !important'
                                },
                                '& label:has(input[value=""])': {
                                    display: 'none !important'
                                }
                            })
                        }}
                    />
                </Box>
                {showDescription && (
                    <FormHelperText>
                        {description}
                    </FormHelperText>
                )}
                {showError && (
                    <FormHelperText error={true}>
                        {computedError}
                    </FormHelperText>
                )}
            </FormControl>
        </Box>
    );
};

Rating.displayName = 'Rating';

export default Rating;
